/**
 * Allow us to manage feature flags on the JS code
 */
export default class FeatureFlags {
    constructor(defaultFlags = {}) {
        this.flags = defaultFlags;
        this.flagListeners = {};
    }

    /**
     * Enable a group of flags
     * @param flags An object containing the name of the flag as key and the fmag value as value
     */
    enableFlags(flags = {}) {
        Object.assign(this.flags, flags);

        Object.keys(flags).forEach(flag => {
            this.triggerListeners(flag, {
                type: 'enable',
                value: flags[flag],
            });
        });
    }

    /**
     * Enable a flag
     * @param flag The flag name
     * @param value The flag value. By default set to true
     */
    enable(flag, value = true) {
        this.flags[flag] = value;

        this.triggerListeners(flag, {
            type: 'enable',
            value,
        });
    }

    /**
     * Disable a flag
     * @param flag The flag name
     */
    disable(flag) {
        delete this.flags[flag];

        this.triggerListeners(flag, { type: 'disable' });
    }

    /**
     * Check if a flag exists
     * @param flagToCheck The name of the flag to check
     * @returns {boolean} True if the flag exist
     */
    exists(flagToCheck) {
        return Object.prototype.hasOwnProperty.call(this.flags, flagToCheck);
    }

    /**
     * Check if a flag is enabled
     * @param flagToCheck The name of the flag to check
     * @returns {boolean} True if the flag has a value other than false, null or undefined
     */
    isEnabled(flagToCheck) {
        const flag = this.flags[flagToCheck];
        return flag !== null && flag !== undefined && flag !== false;
    }

    /**
     * Get the value of the flag
     * @param flag The name of the flag
     * @param defaultValue The default value if the key doesn't exists
     * @returns {*} The value associated to this flag or undefined if the flag is disabled.
     */
    getValue(flag, defaultValue) {
        return this.flags[flag] || defaultValue;
    }

    /**
     * Get all values of the flag by domain
     * @param domain The name of the domain
     * @returns {*} The values associated to domain.
     */
    getByDomain(domain = '') {
        return Object.entries(this.flags).reduce((flags, [key, value]) => {
            if (key.startsWith(`${domain}.`)) {
                return {
                    ...flags,
                    [key]: value,
                };
            }
            return flags;
        }, {});
    }

    /**
     * Listen flag on change
     * @param flag The name of the flag
     * @param callback the callback to called when flag change
     */
    addListener(flag, callback) {
        if (!this.flagListeners[flag]) {
            this.flagListeners[flag] = [];
        }

        this.flagListeners[flag].push(callback);
    }

    /**
     * Listen all flags on change
     * @param callback the callback to called when any flag change
     */
    addAllListener(callback) {
        if (!this.flagListeners.__ALL__) {
            this.flagListeners.__ALL__ = [];
        }

        this.flagListeners.__ALL__.push(callback);
    }

    /**
     * Remove flag listener
     * @param flag The name of the flag
     * @param callback the callback to called when flag change
     */
    removeListener(flag, callback) {
        if (this.flagListeners[flag]) {
            const index = this.flagListeners[flag].indexOf(callback);
            this.flagListeners[flag].splice(index, 1);
        }
    }

    /**
     * Remove flag listener
     * @param flag The name of the flag
     * @param callback the callback to called when flag change
     */
    removeAllListener(callback) {
        if (this.flagListeners.__ALL__) {
            const index = this.flagListeners.__ALL__.indexOf(callback);
            this.flagListeners.__ALL__.splice(index, 1);
        }
    }

    /**
     * Trigger all listeners by flag
     * @param flag The name of the flag
     * @param data data to forward in callback listener
     */
    triggerListeners(flag, data) {
        if (this.flagListeners[flag] && !!this.flagListeners[flag].length) {
            this.flagListeners[flag].forEach(listener => listener(data));
        }
        if (this.flagListeners.__ALL__ && !!this.flagListeners.__ALL__) {
            this.flagListeners.__ALL__.forEach(listener =>
                listener(flag, data),
            );
        }
    }
}
